/**
 * Hover to preview, click to keep — and the gap in between, which is the whole
 * problem.
 *
 * A panel that opens on hover and closes on leave is two lines until you try to
 * *use* it. The pointer has to cross the space between the button and the panel,
 * and the naive version closes the moment it leaves the button — so the panel
 * vanishes on the way to it, every time, and nothing about the code looks wrong.
 *
 * Two rules fix it and both are needed:
 *
 * - **The surface is part of the hover region.** Entering the panel cancels the
 *   pending close, so the panel keeps itself open.
 * - **Leaving schedules rather than closes.** The gap is crossed in a few tens of
 *   milliseconds, and without a grace period the cancel arrives after the close.
 *
 * ## Opening is delayed too, and for a different reason
 *
 * The key sits in a toolbar row. Sweeping the pointer across it on the way to the
 * symbol box would flash a panel open behind everything, so an open waits long
 * enough to mean it — short enough that a deliberate hover still feels immediate.
 *
 * ## Pinning is what makes it usable rather than a peek
 *
 * A hover panel cannot be dragged, detached or read for more than a moment: the
 * pointer has to stay in it. A click pins it, and a pinned panel ignores the
 * pointer entirely until it is dismissed.
 *
 * ## Touch has no hover
 *
 * `pointerenter` fires for a tap on every engine that synthesises mouse events,
 * so on a touch screen a tap would open on hover *and* pin on click, and the
 * second toggle would close what the first opened. Only a mouse hovers.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** Long enough to mean it; short enough that a deliberate hover feels instant. */
export const OPEN_DELAY_MS = 140;
/** Long enough to cross the gap between the trigger and the panel. */
export const CLOSE_DELAY_MS = 220;

export interface HoverIntentOptions {
  openDelay?: number;
  closeDelay?: number;
  /** Called when the panel opens, so a caller can record what it opened for. */
  onOpen?: () => void;
  /** Called when it closes for any reason. */
  onClose?: () => void;
}

export interface HoverIntent {
  /** Whether the panel should be on screen at all. */
  shown: boolean;
  /** Whether it is being kept open rather than hovered. */
  pinned: boolean;
  /** The pointer entered the trigger. */
  enterTrigger: (pointerType?: string) => void;
  /** The pointer left the trigger. */
  leaveTrigger: () => void;
  /** The pointer entered the panel — cancels a pending close. */
  enterSurface: () => void;
  /** The pointer left the panel. */
  leaveSurface: () => void;
  /** The trigger was clicked: pin an open panel, or close a pinned one. */
  togglePin: () => void;
  /** Dismiss, however it is open. */
  dismiss: () => void;
}

export function useHoverIntent(options: HoverIntentOptions = {}): HoverIntent {
  const {
    openDelay = OPEN_DELAY_MS,
    closeDelay = CLOSE_DELAY_MS,
    onOpen,
    onClose,
  } = options;

  const [shown, setShown] = useState(false);
  const [pinned, setPinned] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  // Read through refs so a caller passing inline arrows — which is every caller —
  // cannot make these callbacks part of anything's identity.
  const openRef = useRef(onOpen);
  const closeRef = useRef(onClose);
  openRef.current = onOpen;
  closeRef.current = onClose;

  const clear = useCallback(() => {
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
    }
  }, []);

  // A pending open or close outliving the component would fire into a tree that
  // is gone.
  useEffect(() => clear, [clear]);

  const enterTrigger = useCallback(
    (pointerType?: string) => {
      // Touch and pen synthesise an enter for a tap. Treating that as a hover
      // opens the panel and then the click toggles it shut again.
      if (pointerType !== undefined && pointerType !== "mouse") return;
      clear();
      setShown((already) => {
        if (already) return true;
        timer.current = window.setTimeout(() => {
          setShown(true);
          openRef.current?.();
        }, openDelay);
        return false;
      });
    },
    [clear, openDelay],
  );

  const scheduleClose = useCallback(() => {
    clear();
    timer.current = window.setTimeout(() => {
      setShown(false);
      closeRef.current?.();
    }, closeDelay);
  }, [clear, closeDelay]);

  const leaveTrigger = useCallback(() => {
    // A pinned panel is not the pointer's business any more.
    setPinned((isPinned) => {
      if (!isPinned) scheduleClose();
      return isPinned;
    });
  }, [scheduleClose]);

  const enterSurface = useCallback(() => clear(), [clear]);

  const leaveSurface = useCallback(() => {
    setPinned((isPinned) => {
      if (!isPinned) scheduleClose();
      return isPinned;
    });
  }, [scheduleClose]);

  const togglePin = useCallback(() => {
    clear();
    setPinned((wasPinned) => {
      if (wasPinned) {
        setShown(false);
        closeRef.current?.();
        return false;
      }
      // A click before the open delay elapsed still pins: the user asked for the
      // panel twice, and making them wait for a preview they have already
      // committed to would read as a dropped click.
      setShown((already) => {
        if (!already) openRef.current?.();
        return true;
      });
      return true;
    });
  }, [clear]);

  const dismiss = useCallback(() => {
    clear();
    setPinned(false);
    setShown(false);
    closeRef.current?.();
  }, [clear]);

  return {
    shown,
    pinned,
    enterTrigger,
    leaveTrigger,
    enterSurface,
    leaveSurface,
    togglePin,
    dismiss,
  };
}
